import React from 'react';
import { Button } from '../../ui/button';
import { Edit, Trash2 } from 'lucide-react';
import RevisionMeta from './RevisionMeta';
import LessonAIBuilder from './LessonAIBuilder';
import { stripAndTruncate } from '../../../lib/richText';

/**
 * LessonCard — one row inside the module Peek view. Renders lesson meta,
 * Edit/Delete actions, AND (new) a per-lesson AI Generate panel so
 * admins can pick body / quiz / takeaways / checklist / poll / sources /
 * diagram element toggles for THIS specific lesson without leaving the
 * peek. Collapsed by default — sits below the row so the module's
 * "Generate content for all N lessons" bulk panel stays the primary
 * flow when the admin just wants everything filled in one shot.
 */
const LessonCard = ({
  lesson,
  lessonIndex,
  onEdit,
  onDelete,
  onGenerated,
  selectable = false,
  selected = false,
  onToggleSelect,
}) => {
  // Prefer the AI-generated hook/blurb; fall back to the first ~180
  // chars of the rich body so the card always shows *something*
  // instead of a bare title.
  const blurb =
    lesson.hook ||
    lesson.short_description ||
    (lesson.description ? stripAndTruncate(lesson.description, 200) : '');
  return (
    <div
      className={`p-3 bg-white dark:bg-slate-700 rounded border ${
        selectable && selected
          ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
          : 'border-slate-200 dark:border-slate-600'
      }`}
      data-testid={`lesson-card-${lesson.id}`}
    >
      <div className="flex items-center justify-between">
        {selectable && (
          <label
            className="mr-3 shrink-0 cursor-pointer inline-flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            title={selected ? 'Deselect this lesson' : 'Select this lesson for bulk actions'}
          >
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              className="w-4 h-4 rounded border-slate-400 accent-blue-600"
              data-testid={`lesson-select-checkbox-${lesson.id}`}
            />
          </label>
        )}
        <div className="flex-1 min-w-0 pr-3">
          <p className="font-medium text-slate-900 dark:text-white">
            Lesson {lessonIndex + 1}: {lesson.title}
          </p>
          {blurb && (
            <p
              className="mt-1 text-xs text-slate-600 dark:text-neutral-300 line-clamp-2"
              data-testid={`lesson-blurb-${lesson.id}`}
            >
              {blurb}
            </p>
          )}
          {lesson.learning_objective && (
            <p
              className="mt-1 text-[11px] text-slate-500 dark:text-neutral-400 italic line-clamp-1"
              data-testid={`lesson-objective-${lesson.id}`}
            >
              Objective: {lesson.learning_objective}
            </p>
          )}
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-slate-600 dark:text-neutral-400 capitalize">
              {lesson.content_type}
            </span>
            {lesson.duration_minutes && (
              <span className="text-xs text-slate-600 dark:text-neutral-400">
                {lesson.duration_minutes}m
              </span>
            )}
            {lesson.has_quiz && (
              <span className="text-xs font-semibold text-amber-600">
                Quiz
              </span>
            )}
            {lesson.ad_enabled && (
              <span className="text-xs font-semibold text-blue-600">
                Ad ({lesson.ad_position})
              </span>
            )}
          </div>
          <RevisionMeta
            className="mt-1.5"
            size="xs"
            createdAt={lesson.created_at}
            publishedAt={lesson.published_at}
            reviewBy={lesson.review_by}
            testIdPrefix={`lesson-revision-${lesson.id}`}
          />
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Per-lesson AI Generate panel — same element checklist as the
          module bulk panel, scoped to this one lesson only. Uses the
          existing /admin/ai/lesson-content/start endpoint so it queues,
          polls, and refreshes exactly like the batch flow. */}
      <div className="mt-3">
        <LessonAIBuilder
          lessonId={lesson.id}
          lessonTitle={lesson.title}
          lesson={lesson}
          onGenerated={onGenerated}
        />
      </div>
      {/* At-a-glance badge row — mirrors the AI Builder's internal state
          so admins can spot generated/final lessons without expanding
          each row's panel. */}
      {(lesson.content_generated || lesson.ai_content_final) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`lesson-ai-badges-${lesson.id}`}>
          {lesson.content_generated && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
              data-testid={`lesson-generated-badge-${lesson.id}`}
              title={lesson.ai_content_generated_at ? `AI-generated on ${new Date(lesson.ai_content_generated_at).toLocaleString()}` : 'AI content generated'}
            >
              ✓ AI generated{lesson.ai_content_generated_at ? ` · ${new Date(lesson.ai_content_generated_at).toLocaleDateString()}` : ''}
            </span>
          )}
          {lesson.ai_content_final && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
              data-testid={`lesson-final-badge-${lesson.id}`}
              title="Manually marked as final"
            >
              ● Final
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default LessonCard;
